import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { COLORS, CSS, FONT, OUTLINE } from '../theme';
import { Character } from '../objects/Character';
import { Hat } from '../objects/Hat';
import { DanceSystem } from '../systems/DanceSystem';
import { BlinkSystem } from '../systems/BlinkSystem';
import { ThrowSystem } from '../systems/ThrowSystem';
import { HitDetection } from '../systems/HitDetection';
import {
  STAGES,
  StageData,
  getAccessoryForStage,
  getStage,
  scoreForResult,
  starsForHitType,
} from '../data/stages';
import { stageProgress, formatCooldown } from '../state/StageProgress';
import { LandingResult, StageResult } from '../types';
import { drawSpotlight, drawHypeFlash } from '../ui/Spotlight';
import { EqualizerBg } from '../ui/EqualizerBg';
import { HatPad } from '../ui/HatPad';
import { JudgmentFlash } from '../ui/JudgmentFlash';
import { spawnConfetti } from '../ui/Confetti';
import { ProgressBeads } from '../ui/ProgressBeads';
import { makePill, makeCompoundPill } from '../ui/Pill';
import { drawOutlinedRect } from '../ui/OutlinedShape';
import { setLetterboxColor } from '../utils/letterbox';
import { getLayoutFrame } from '../utils/layout';

const REFILL_POINT_COST = 100;
const REFILL_AD_DURATION_MS = 3000;
/** How long a stage stays locked after a full failed run. The player can pay
 * (ad/points) to skip this wait, or wait it out for a free retry. */
const COOLDOWN_MS = 3 * 60 * 60 * 1000; // 3 hours

// Result-popup motion. All hit tiers share one compact size so PERFECT no
// longer overflows down onto the character's head — color, subtitle, and
// the PERFECT-only stroke pulse + star burst carry the tier differentiation
// instead. MISS keeps a slightly different shape (Quad.Out, no bounce) so a
// failed toss reads as flatter than a hit.
const RESULT_POPUP_HIT = {
  scale: 0.9,
  duration: 220,
  ease: 'Back.Out',
  fontSize: 54,
  dwell: 0,
  angle: -4,
} as const;
const RESULT_POPUP_TIERS = {
  perfect: RESULT_POPUP_HIT,
  good: RESULT_POPUP_HIT,
  just: RESULT_POPUP_HIT,
  miss: { scale: 0.95, duration: 200, ease: 'Quad.Out', fontSize: 58, dwell: 900, angle: 0 },
} as const;

// Equalizer amplitude levels — base is a subdued idle pulse, hit pumps it up
// so the celebration reads. Stays amped until the scene transitions (the user
// drives the transition via TAP TO CONTINUE, so there is no auto-restore).
const EQ_LEVEL_IDLE = 0.45;
const EQ_LEVEL_HIT = 1.2;

export class GameScene extends Phaser.Scene {
  character!: Character;
  hat!: Hat;
  danceSystem!: DanceSystem;
  blinkSystem!: BlinkSystem;
  throwSystem!: ThrowSystem;

  // HUD
  private stageBadge!: Phaser.GameObjects.Container;
  /** Compound pill at top-right showing "current | TOSS | total". Rebuilt on
   * every toss in refreshHud — makeCompoundPill's inner Text children are
   * hard to address by index without coupling to its internal layout. */
  private tossPill: Phaser.GameObjects.Container | null = null;
  /** Canvas-x of the back pill's visible left edge, captured once at create()
   * time. refreshTossPill mirrors this gap on the right side so the toss pill's
   * right edge sits the same distance from the right canvas edge as the back
   * pill sits from the left. */
  private backPillLeftEdge = 28;
  private beads!: ProgressBeads;
  private equalizer!: EqualizerBg;
  private spotlight!: Phaser.GameObjects.Container;
  private hatPad!: HatPad;
  private tossHint!: Phaser.GameObjects.Text;
  private judgmentFlash!: JudgmentFlash;
  private resultPopup: Phaser.GameObjects.Container | null = null;
  private resultClearEvent: Phaser.Time.TimerEvent | null = null;

  // State
  private stage!: StageData;
  private throwsUsed = 0;
  private missCount = 0;
  private endingAttempt = false;
  private modalOpen = false;

  constructor() {
    super('GameScene');
  }

  init(data: { stageId?: string }) {
    const fallback = STAGES[0];
    const stage = (data?.stageId && getStage(data.stageId)) || fallback;
    this.stage = stage;
    this.throwsUsed = 0;
    this.missCount = 0;
    this.endingAttempt = false;
    this.modalOpen = false;
  }

  create() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    this.drawBackdrop();

    const accessory = getAccessoryForStage(this.stage);

    // ───── stage scene effects (behind character) ─────
    // Layered backdrop: equalizer bars frame the body, yolk spotlight pools
    // warmth around the character, coral hype-flash pulses behind both.
    // Y values are tuned to follow CHAR_CENTER_Y; bump together if the
    // character moves.
    this.equalizer = new EqualizerBg(this, STAGE_W / 2, 550, STAGE_W - 40, 460, 22, EQ_LEVEL_IDLE);
    drawHypeFlash(this, STAGE_W / 2, 835, 360, 220).setDepth(3);
    this.spotlight = drawSpotlight(this, STAGE_W / 2, 695, 280, 220);
    this.spotlight.setDepth(4);
    this.judgmentFlash = new JudgmentFlash(this);

    // ───── characters ─────
    this.character = new Character(this);
    this.hat = new Hat(this, accessory);

    this.danceSystem = new DanceSystem(this.character, {
      bpm: this.stage.bpm,
      maxAngle: this.stage.maxAngle,
      lateralPx: this.stage.swayLateralPx ?? 0,
      depthPx: this.stage.swayDepthPx ?? 0,
      phaseRatio: this.stage.swayPhaseRatio ?? 0.5,
    });
    this.blinkSystem = new BlinkSystem(this, this.character);
    this.throwSystem = new ThrowSystem(this, this.hat, this.character);
    this.throwSystem.hitWindowMult = this.stage.hitWindowMult;

    // ───── hat pad behind hat ─────
    this.hatPad = new HatPad(this, GAME_CONFIG.HAT_REST_X, GAME_CONFIG.HAT_REST_Y, 220);

    // ───── back pill (top-left corner) ─────
    // Built before the top HUD so refreshTossPill (called from buildTopHud)
    // can mirror the back pill's left-edge offset onto the toss pill's right
    // edge. Sits at the same y/height as the stage badge for clean alignment.
    // Uses the universal "back" glyph (←) so it reads as "return to stage
    // select" rather than "abandon game".
    const backPill = makePill(this, 60, frame.fromTop(78), {
      text: '←',
      textColor: CSS.white,
      textSize: 28,
      fontWeight: '800',
      padX: 18,
      padY: 14,
      fill: COLORS.ink,
      fillAlpha: 0.55,
      border: COLORS.white,
      borderAlpha: 0.3,
      borderThickness: 2,
    });
    backPill.setDepth(950);
    // Capture the visible left edge so refreshTossPill can mirror the gap on
    // the right side. The Text child's width + padX*2 equals the pill's full
    // width (see makePill); the pill is centered on x=60.
    let backPillW = 56;
    backPill.list.forEach((child) => {
      if (child instanceof Phaser.GameObjects.Text) {
        backPillW = child.width + 18 * 2;
      }
    });
    this.backPillLeftEdge = 60 - backPillW / 2;

    // Separate Zone for hit detection — the ThrowSystem owns scene-level
    // pointerdown/up handlers for hat flicks, and Container-with-rect hit
    // areas were getting swallowed. A dedicated Zone above everything gives
    // the back button a reliable, finger-friendly tap target.
    const backHit = this.add
      .zone(60, frame.fromTop(78), 110, 84)
      .setInteractive({ useHandCursor: true });
    backHit.setDepth(951);
    backHit.on('pointerup', () => this.scene.start('StageSelectScene'));

    // ───── top HUD: stage badge + toss-count pill ─────
    this.buildTopHud();

    // ───── progress beads ─────
    this.beads = new ProgressBeads(this, STAGE_W / 2, frame.fromTop(180), this.stage.throwLimit);
    this.beads.container.setDepth(900); // ensure above scenery

    // ───── flick hint below pad ─────
    // HatPad center y=HAT_REST_Y + radius 110 + dashed-ring band 14 = bottom edge.
    // Placed ~155px below the pad center to sit just under the dashed ring.
    this.tossHint = this.add
      .text(STAGE_W / 2, GAME_CONFIG.HAT_REST_Y + 155, '↑  Toss it', {
        fontFamily: FONT.display,
        fontSize: '28px',
        color: CSS.white,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setDepth(800)
      .setLetterSpacing(3);

    this.hat.onLand = () => this.handleLanding();

    // If the player entered a stage that is still in cooldown (e.g. they tapped
    // a cooled-down card from stage select), show the refill modal right away
    // so they can either pay to skip or back out. Mark all tosses as used so
    // the UI reads consistently behind the modal.
    if (stageProgress.isCoolingDown(this.stage.id)) {
      this.throwsUsed = this.stage.throwLimit;
      this.endingAttempt = true;
      this.refreshHud();
      this.openOutOfLivesModal();
    }
  }

  private drawBackdrop() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    setLetterboxColor(COLORS.ink);
    const gfx = this.add.graphics();
    gfx.setDepth(0);
    gfx.fillStyle(COLORS.ink, 1);
    gfx.fillRect(0, 0, STAGE_W, STAGE_H);
  }


  private buildTopHud() {
    const frame = getLayoutFrame(this);
    // Stage badge — outlined card with stage name. Sits to the right of the
    // back pill; the back pill's hit zone now extends to ~x=115, so the
    // stage pill starts at x=210 to leave a clean visible gap.
    const stagePill = makePill(this, 220, frame.fromTop(78), {
      text: this.stage.name,
      textSize: 26,
      textColor: CSS.white,
      fontWeight: '800',
      padX: 24,
      padY: 14,
      fill: COLORS.white,
      fillAlpha: 0.12,
      borderThickness: 2,
      border: COLORS.white,
      borderAlpha: 0.25,
    });
    stagePill.setDepth(900);
    this.stageBadge = stagePill;

    // Toss-count pill at top-right (where the BPM pill used to live).
    // BPM is dropped — the game isn't rhythm-graded, so the readout was
    // decorative; the toss counter is the meaningful number to surface.
    this.refreshTossPill();
  }

  /** Build (or rebuild) the top-right toss pill. The compound layout mirrors
   * the old BPM pill's `[num] | [label] | [num]` rhythm so the HUD silhouette
   * stays consistent. Called from buildTopHud at scene start and from
   * refreshHud after each toss. */
  private refreshTossPill() {
    if (this.tossPill) {
      this.tossPill.destroy();
      this.tossPill = null;
    }
    const frame = getLayoutFrame(this);
    const current = Math.min(this.throwsUsed + 1, this.stage.throwLimit);
    const padX = 22;
    const padY = 12;
    // Build at a placeholder x; we shift to the mirrored position below once
    // the actual width is known (digit glyphs vary slightly in width).
    this.tossPill = makeCompoundPill(
      this,
      540,
      frame.fromTop(78),
      [
        { text: `${current}`, color: CSS.yolk, size: 34, weight: '800', tabular: true },
        { text: 'TOSS', color: CSS.whiteSoft, size: 18, weight: '700' },
        { text: `${this.stage.throwLimit}`, color: CSS.whiteSoft, size: 18, weight: '700', tabular: true },
      ],
      { fill: COLORS.white, fillAlpha: 0.1, padX, padY, border: COLORS.white, borderAlpha: 0.2 },
    );

    // Mirror the back pill's left-edge gap onto the right side. Walk the
    // container's Text children to recover the pill's total width: same
    // formula as makeCompoundPill (sum text widths + (n-1)*gap + padX*2).
    const gap = 16;
    let textTotal = 0;
    let textCount = 0;
    this.tossPill.list.forEach((child) => {
      if (child instanceof Phaser.GameObjects.Text) {
        textTotal += child.width;
        textCount++;
      }
    });
    const pillW = textTotal + Math.max(0, textCount - 1) * gap + padX * 2;
    const targetCenterX = GAME_CONFIG.STAGE_W - this.backPillLeftEdge - pillW / 2;
    this.tossPill.setX(targetCenterX);

    this.tossPill.setDepth(900);
  }

  private refreshHud() {
    this.beads.render(this.throwsUsed);
    this.refreshTossPill();
  }

  update(time: number, _delta: number) {
    this.danceSystem.update(time);
    this.hat.updateFlight(time);
  }

  private handleLanding() {
    const hit = HitDetection.check(this.hat, this.character);
    let result: LandingResult;
    if (hit === 'perfect') result = 'perfect';
    else if (hit === 'good') result = 'good';
    else if (hit === 'just') result = 'just';
    else result = 'miss';

    this.throwsUsed += 1;
    if (result === 'miss') this.missCount += 1;

    this.judgmentFlash.flash(result);

    const isHit = result === 'perfect' || result === 'good' || result === 'just';
    this.showResult(result, { autoDismiss: !isHit });

    if (result === 'miss') {
      this.character.showFailFace();
      this.danceSystem.suspendLateral();
    }

    if (isHit) {
      const hitType: 'perfect' | 'good' | 'just' =
        result === 'perfect' ? 'perfect' : result === 'good' ? 'good' : 'just';
      this.character.setHead(this.hat.getMatchingHeadKey());
      this.danceSystem.setHappy();
      this.hat.hideAfterHit();
      // Clear the pad + its infinite ring pulse and the "↑ Toss it" hint so
      // none of them bleed through under the TAP TO CONTINUE pill.
      this.hatPad.destroy();
      this.tossHint.destroy();
      this.endingAttempt = true;
      this.playEncore(hitType);
      this.showContinuePrompt(hitType);
      return;
    }

    const outOfThrows = this.throwsUsed >= this.stage.throwLimit;
    if (outOfThrows) {
      this.endingAttempt = true;
      this.time.delayedCall(900, () => this.failAttempt());
    } else {
      this.refreshHud();
      this.time.delayedCall(800, () => {
        if (this.endingAttempt) return;
        this.hat.reset();
      });
    }
  }

  /**
   * Visual celebration kicked off on a successful throw. Layers confetti,
   * a spotlight pulse, and an equalizer level bump on top of the already-
   * running happy dance / new accessory / popup card. The player drives the
   * transition into ResultScene by tapping TAP TO CONTINUE — there is no
   * timed dwell or camera fade here. EQ stays amped until scene transition.
   */
  private playEncore(hitType: 'perfect' | 'good' | 'just') {
    spawnConfetti(this, hitType);

    // Spotlight pulse — single yoyo scale-up around the spotlight ellipse.
    this.tweens.add({
      targets: this.spotlight,
      scaleX: 1.25,
      scaleY: 1.25,
      duration: 280,
      ease: 'Quad.Out',
      yoyo: true,
    });

    // Equalizer level bumps from subdued idle (0.45) to hit (1.2). The new
    // amplitude phases in over the bars' staggered 0–900ms delays so the
    // boost reads as a build, not a snap.
    this.equalizer.setLevel(EQ_LEVEL_HIT);
  }

  private finishAttempt(hitType: 'perfect' | 'good' | 'just') {
    const hitThrowNumber = this.throwsUsed;
    const earnedScore = scoreForResult(hitType);
    const stars = starsForHitType(hitType);

    stageProgress.recordAttempt(this.stage.id, earnedScore, stars);
    const cumulative = stageProgress.getRecord(this.stage.id).cumulativeScore;

    const result: StageResult = {
      stageId: this.stage.id,
      earnedScore,
      cumulativeScore: cumulative,
      stars,
      hitThrowNumber,
      hitType,
      missCount: this.missCount,
      cleared: true,
    };
    this.scene.start('ResultScene', { result });
  }

  private failAttempt() {
    // Set cooldown BEFORE opening the modal — even if the user dismisses the
    // modal by closing the tab or hitting the X exit, the bypass path
    // (return to stage select → re-pick stage → free retry) is now blocked.
    stageProgress.setCooldown(this.stage.id, COOLDOWN_MS);
    stageProgress.recordAttempt(this.stage.id, 0, 0);
    this.openOutOfLivesModal();
  }

  private showResult(result: LandingResult, options: { autoDismiss?: boolean } = {}) {
    const autoDismiss = options.autoDismiss !== false;
    if (this.resultPopup) this.resultPopup.destroy();
    if (this.resultClearEvent) this.resultClearEvent.remove();

    const { STAGE_W } = GAME_CONFIG;
    const cx = STAGE_W / 2;
    const frame = getLayoutFrame(this);
    const beadsY = frame.fromTop(180);
    const charHeadY = GAME_CONFIG.CHAR_CENTER_Y - 280;
    const cy = (beadsY + charHeadY) / 2;

    let label: string;
    let fill: number;
    let textColor: string;
    let subtitle = '';
    let subColor = CSS.yolk;

    if (result === 'perfect') {
      label = 'PERFECT!';
      fill = COLORS.yolk;
      textColor = CSS.ink;
      subtitle = `+${scoreForResult('perfect')} · 3★`;
      subColor = CSS.yolk;
    } else if (result === 'good') {
      label = 'GOOD!';
      fill = COLORS.emerald;
      textColor = CSS.ink;
      subtitle = `+${scoreForResult('good')} · 2★`;
      subColor = CSS.emerald;
    } else if (result === 'just') {
      label = 'JUST!';
      fill = COLORS.cream;
      textColor = CSS.ink;
      subtitle = `+${scoreForResult('just')} · 1★`;
      subColor = CSS.cream;
    } else {
      label = 'MISS';
      fill = COLORS.coral;
      textColor = CSS.white;
      subtitle = '';
      subColor = CSS.coral;
    }

    const tier = RESULT_POPUP_TIERS[result];

    const container = this.add.container(cx, cy);
    container.setDepth(950);
    container.setAngle(tier.angle);

    // Measure label first to size the pill body.
    const text = this.add
      .text(0, 0, label, {
        fontFamily: FONT.display,
        fontSize: `${tier.fontSize}px`,
        color: textColor,
        fontStyle: '900',
      })
      .setOrigin(0.5);
    const padX = 38;
    const padY = 18;
    const w = text.width + padX * 2;
    const h = text.height + padY * 2;
    const radius = 32;

    const shadow = this.add.graphics();
    shadow.fillStyle(COLORS.ink, 1);
    shadow.fillRoundedRect(-w / 2, -h / 2 + 10, w, h, radius);
    container.add(shadow);

    const body = this.add.graphics();
    body.fillStyle(fill, 1);
    body.fillRoundedRect(-w / 2, -h / 2, w, h, radius);
    body.lineStyle(OUTLINE, COLORS.ink, 1);
    body.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);
    container.add(body);
    container.add(text);

    if (subtitle) {
      const sub = this.add
        .text(0, h / 2 + 22, subtitle, {
          fontFamily: FONT.display,
          fontSize: '30px',
          color: subColor,
          fontStyle: '800',
          stroke: CSS.ink,
          strokeThickness: 4,
        })
        .setOrigin(0.5);
      container.add(sub);
    }

    container.setScale(0.4);
    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: tier.scale,
      alpha: 1,
      duration: tier.duration,
      ease: tier.ease,
    });

    if (result === 'perfect') {
      // B: stroke pulse — a thick ink outline flashes once over the body right
      // after the entry tween settles. Drawn as a separate overlay so the body
      // graphics doesn't need to be redrawn.
      const pulse = this.add.graphics();
      pulse.lineStyle(OUTLINE * 2.2, COLORS.ink, 1);
      pulse.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);
      pulse.setAlpha(0);
      container.add(pulse);
      this.tweens.add({
        targets: pulse,
        alpha: 1,
        duration: 70,
        delay: tier.duration - 40,
        yoyo: true,
        onComplete: () => pulse.destroy(),
      });

      // F: star particles — eight ★ characters fan out from the pill center,
      // pop in with Back.Out, then drift + fade. Sit one depth above the popup.
      const count = 8;
      for (let i = 0; i < count; i++) {
        const baseAngle = (i / count) * Math.PI * 2;
        const angle = baseAngle + (Math.random() - 0.5) * 0.4;
        const dist = 100 + Math.random() * 40;
        const tx = cx + Math.cos(angle) * dist;
        const ty = cy + Math.sin(angle) * dist;
        const star = this.add
          .text(cx, cy, '★', {
            fontFamily: FONT.display,
            fontSize: '34px',
            color: CSS.yolk,
            fontStyle: '900',
            stroke: CSS.ink,
            strokeThickness: 3,
          })
          .setOrigin(0.5)
          .setDepth(951)
          .setAlpha(0)
          .setScale(0.3);
        this.tweens.add({
          targets: star,
          x: tx,
          y: ty,
          alpha: 1,
          scale: 1,
          angle: (Math.random() - 0.5) * 540,
          duration: 280,
          ease: 'Back.Out',
        });
        this.tweens.add({
          targets: star,
          alpha: 0,
          scale: 0.4,
          duration: 420,
          delay: 360,
          ease: 'Quad.In',
          onComplete: () => star.destroy(),
        });
      }
    }

    if (result === 'miss') {
      // E: damped horizontal shake — pill wiggles ±6px on Sine wave that decays
      // to zero. Starts after the entry tween finishes so the entrance reads
      // first, then the failure tremor kicks in.
      this.tweens.addCounter({
        from: 0,
        to: 1,
        duration: 240,
        delay: tier.duration,
        onUpdate: (tween) => {
          if (!container.active) return;
          const t = tween.getValue() ?? 0;
          const wave = Math.sin(t * Math.PI * 4) * 6 * (1 - t);
          container.x = cx + wave;
        },
        onComplete: () => {
          if (container.active) container.x = cx;
        },
      });
    }

    this.resultPopup = container;
    if (autoDismiss) {
      this.resultClearEvent = this.time.delayedCall(tier.dwell, () => {
        if (!this.resultPopup) return;
        this.tweens.add({
          targets: this.resultPopup,
          alpha: 0,
          scale: tier.scale * 0.9,
          duration: 250,
          onComplete: () => {
            if (this.resultPopup) {
              this.resultPopup.destroy();
              this.resultPopup = null;
            }
          },
        });
      });
    }
  }

  /**
   * After a hit, the result card stays put (autoDismiss=false) and this method
   * lays down two parallel ways to advance to ResultScene:
   *
   * 1. An explicit pill button (yolk fill + ink stroke) near the bottom of the
   *    canvas, depth 960 — the primary affordance. Pulses gently after fade-in
   *    so it reads as interactive without yelling.
   * 2. A full-canvas invisible tap zone behind the popup (depth 948) — taps
   *    anywhere on the screen also advance, so the player never has to hunt
   *    for the button.
   *
   * Both paths funnel through a single `advance()` that's idempotent (the
   * `consumed` flag) so a double-tap or button+background race cannot
   * double-trigger finishAttempt. The result card itself is non-interactive,
   * so tap-through from the card hits the tap zone underneath.
   */
  private showContinuePrompt(hitType: 'perfect' | 'good' | 'just') {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const frame = getLayoutFrame(this);

    let consumed = false;
    const advance = () => {
      if (consumed) return;
      consumed = true;
      tapZone.destroy();
      buttonContainer.destroy();
      this.finishAttempt(hitType);
    };

    // Listen on pointerup (not pointerdown). The ResultScene's RETRY/NEXT
    // buttons also fire on pointerup, so if we advanced on pointerdown the
    // user's release would propagate to whichever button landed under their
    // finger on the next scene and instantly re-trigger.
    const tapZone = this.add
      .rectangle(0, 0, STAGE_W, STAGE_H, 0x000000, 0)
      .setOrigin(0, 0)
      .setDepth(948)
      .setInteractive();
    tapZone.on('pointerup', advance);

    const buttonW = 380;
    const buttonH = 100;
    const radius = 32;
    const buttonContainer = this.add.container(STAGE_W / 2, frame.fromBottom(220));
    buttonContainer.setDepth(960);
    buttonContainer.setAlpha(0);

    const shadow = this.add.graphics();
    shadow.fillStyle(COLORS.ink, 1);
    shadow.fillRoundedRect(-buttonW / 2, -buttonH / 2 + 6, buttonW, buttonH, radius);
    buttonContainer.add(shadow);

    const body = this.add.graphics();
    body.fillStyle(COLORS.yolk, 1);
    body.fillRoundedRect(-buttonW / 2, -buttonH / 2, buttonW, buttonH, radius);
    body.lineStyle(OUTLINE, COLORS.ink, 1);
    body.strokeRoundedRect(-buttonW / 2, -buttonH / 2, buttonW, buttonH, radius);
    buttonContainer.add(body);

    const label = this.add
      .text(0, 0, 'TAP TO CONTINUE', {
        fontFamily: FONT.display,
        fontSize: '36px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5);
    buttonContainer.add(label);

    const buttonHit = this.add
      .rectangle(0, 0, buttonW, buttonH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    buttonContainer.add(buttonHit);
    buttonHit.on('pointerup', advance);

    const settleDelay = 420;
    this.tweens.add({
      targets: buttonContainer,
      alpha: 1,
      duration: 280,
      delay: settleDelay,
    });
    this.tweens.add({
      targets: buttonContainer,
      scale: 1.04,
      duration: 720,
      yoyo: true,
      repeat: -1,
      delay: settleDelay + 280,
      ease: 'Sine.InOut',
    });
  }

  // ─────────────────────────────────────────────
  // Out-of-lives modal — refill paths.
  //
  // The cooldown is set the moment attempts run out (see failAttempt). The
  // modal offers two ways to skip the wait (ad / points) and one "wait it
  // out" exit. The exit path leaves the cooldown active, so returning to
  // stage select cannot bypass the timer.
  // ─────────────────────────────────────────────
  private openOutOfLivesModal() {
    if (this.modalOpen) return;
    this.modalOpen = true;
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    const cy = frame.centerY;

    const overlay = this.add
      .rectangle(0, 0, STAGE_W, STAGE_H, COLORS.ink, 0.7)
      .setOrigin(0, 0)
      .setDepth(1500);
    const panelW = 620;
    const panelH = 640;
    const panel = drawOutlinedRect(this, STAGE_W / 2, cy, panelW, panelH, {
      fill: COLORS.cream,
      radius: 36,
      shadow: 12,
    });
    panel.setDepth(1501);

    const title = this.add
      .text(STAGE_W / 2, cy - panelH / 2 + 68, 'Out of tosses!', {
        fontFamily: FONT.display,
        fontSize: '40px',
        color: CSS.ink,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setDepth(1502);
    const subtitle = this.add
      .text(STAGE_W / 2, cy - panelH / 2 + 124, 'Refill instantly or wait for free', {
        fontFamily: FONT.display,
        fontSize: '22px',
        color: CSS.inkSoft,
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setDepth(1502);

    const created: Phaser.GameObjects.GameObject[] = [overlay, panel, title, subtitle];

    const close = () => {
      created.forEach((o) => o.destroy());
      this.modalOpen = false;
    };

    const makeBtn = (
      yOffset: number,
      label: string,
      sublabel: string,
      fill: number,
      labelColor: string,
      onClick: () => void,
    ) => {
      const bx = STAGE_W / 2;
      const by = cy + yOffset;
      const btnW = panelW - 80;
      const btnH = 108;
      const c = drawOutlinedRect(this, bx, by, btnW, btnH, {
        fill,
        radius: 28,
        shadow: 8,
      });
      c.setDepth(1502);
      const lbl = this.add
        .text(bx, by - 16, label, {
          fontFamily: FONT.display,
          fontSize: '30px',
          color: labelColor,
          fontStyle: '800',
        })
        .setOrigin(0.5)
        .setDepth(1503);
      const sub = this.add
        .text(bx, by + 22, sublabel, {
          fontFamily: FONT.display,
          fontSize: '18px',
          color: labelColor === CSS.white ? CSS.whiteSoft : CSS.inkSoft,
          fontStyle: '700',
        })
        .setOrigin(0.5)
        .setDepth(1503);
      const hit = this.add
        .zone(bx, by, btnW, btnH)
        .setInteractive({ useHandCursor: true })
        .setDepth(1504);
      hit.on('pointerup', onClick);
      created.push(c, lbl, sub, hit);
    };

    const remainingMs = stageProgress.cooldownRemainingMs(this.stage.id);
    const cooldownLabel = remainingMs > 0 ? `Free in ${formatCooldown(remainingMs)}` : 'Come back later';

    makeBtn(
      -82,
      'Watch an ad',
      `Refill ${this.stage.throwLimit} tosses after watching`,
      COLORS.emerald,
      CSS.ink,
      () => {
        this.simulateAd(close);
      },
    );
    makeBtn(
      46,
      `Use points (-${REFILL_POINT_COST}p)`,
      `You have ${stageProgress.getPoints()}p`,
      COLORS.yolk,
      CSS.ink,
      () => {
        if (stageProgress.spendPoints(REFILL_POINT_COST)) {
          stageProgress.clearCooldown(this.stage.id);
          close();
          this.refillThrows();
        } else {
          this.flashMessage('Not enough points');
        }
      },
    );
    makeBtn(174, 'Wait it out', cooldownLabel, COLORS.slate, CSS.white, () => {
      close();
      // Cooldown stays set; stage select will show the countdown badge.
      this.scene.start('StageSelectScene');
    });
  }

  private simulateAd(close: () => void) {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const cy = getLayoutFrame(this).centerY;
    const overlay = this.add
      .rectangle(0, 0, STAGE_W, STAGE_H, COLORS.ink, 0.92)
      .setOrigin(0, 0)
      .setDepth(2000);
    const label = this.add
      .text(STAGE_W / 2, cy - 24, 'Playing ad…', {
        fontFamily: FONT.display,
        fontSize: '40px',
        color: CSS.white,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setDepth(2001);
    let remain = Math.ceil(REFILL_AD_DURATION_MS / 1000);
    const countdown = this.add
      .text(STAGE_W / 2, cy + 46, `${remain}s`, {
        fontFamily: FONT.display,
        fontSize: '32px',
        color: CSS.yolk,
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setDepth(2001);
    const tick = this.time.addEvent({
      delay: 1000,
      repeat: remain - 1,
      callback: () => {
        remain -= 1;
        countdown.setText(`${remain}s`);
      },
    });
    this.time.delayedCall(REFILL_AD_DURATION_MS, () => {
      tick.remove();
      overlay.destroy();
      label.destroy();
      countdown.destroy();
      stageProgress.clearCooldown(this.stage.id);
      close();
      this.refillThrows();
    });
  }

  private refillThrows() {
    this.throwsUsed = 0;
    this.missCount = 0;
    this.endingAttempt = false;
    this.character.reset();
    this.danceSystem.reset();
    this.hat.reset();
    this.refreshHud();
  }

  private flashMessage(message: string) {
    const { STAGE_W } = GAME_CONFIG;
    const cy = getLayoutFrame(this).centerY;
    const text = this.add
      .text(STAGE_W / 2, cy + 260, message, {
        fontFamily: FONT.display,
        fontSize: '26px',
        color: CSS.coral,
        backgroundColor: 'rgba(26,8,38,0.85)',
        fontStyle: '800',
        padding: { x: 22, y: 12 },
      })
      .setOrigin(0.5)
      .setDepth(2100);
    this.tweens.add({
      targets: text,
      alpha: 0,
      duration: 1200,
      delay: 600,
      onComplete: () => text.destroy(),
    });
  }
}
